"use client";

import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { GroqImage } from "@/helpers/Image";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";
import { competitorRichTextDark } from "./richtext";
import { CompetitorIconItem, PageCompetitorSchema } from "@/sanity/pages/PageCompetitorTemplate";

const ease = [0.25, 0.1, 0.25, 1] as const;

function Star() {
  return (
    <svg viewBox="0 0 18 18" fill="none" aria-hidden className="h-[2.25rem] w-[2.25rem] shrink-0">
      <path
        d="M8.99998 1.125L6.44061 6.31125L0.719983 7.13812L4.85998 11.1769L3.88123 16.875L8.99998 14.1862L14.1187 16.875L13.14 11.1769L17.28 7.14375L11.5594 6.31125L8.99998 1.125Z"
        fill="#8FEDB8"
      />
    </svg>
  );
}

function StepCard({ step, gold }: { step: CompetitorIconItem; gold?: boolean }) {
  return (
    <div
      className={cn(
        "flex w-full flex-col items-start gap-[2rem] rounded-[0.5rem] border max-lg:p-[2.5rem] lg:p-[4rem]",
        gold ? "border-[rgba(245,207,99,0.2)] bg-[rgba(245,207,99,0.05)]" : "border-white/[0.08] bg-white/[0.03]",
      )}>
      <div
        className={cn(
          "flex h-[5rem] w-[5rem] shrink-0 items-center justify-center rounded-[0.5rem]",
          gold ? "bg-[#f5cf63]" : "bg-[#8fedb8]",
        )}>
        {step.icon && <GroqImage src={step.icon} alt="" quality={100} className="!h-[2.5rem] !w-[2.5rem] object-contain" />}
      </div>
      {step.title && <p className="mb-0 font-geist text-[2.75rem] font-medium leading-[1.3] text-white">{step.title}</p>}
      {step.body && <p className="mb-0 font-geist text-[2rem] leading-[1.5] text-white/[0.65]">{step.body}</p>}
    </div>
  );
}

export default function SwitchSection({ data }: { data: PageCompetitorSchema }) {
  const steps = data.switchSteps ?? [];
  const alternative = data.switchAlternative;

  if (!data.switchTitle && !steps.length) return null;

  return (
    <section className="w-full bg-[#001b14] px-[2rem] [text-wrap:pretty] pt-[7rem] pb-[8rem] lg:px-0 lg:py-[15rem]">
      <div className="mx-auto flex w-[min(135rem,100%)] flex-col gap-[5rem] lg:flex-row lg:items-start lg:justify-center lg:gap-[15rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease }}
          className="flex w-full flex-col gap-[5rem] max-lg:items-center max-lg:text-center lg:w-[min(60rem,100%)] lg:shrink-0 lg:items-start">
          <div className="flex w-full flex-col gap-[1.375rem] lg:gap-[2rem]">
            {data.switchChip && (
              <p className="mb-0 font-blacker text-[2rem] leading-[1.4] text-white/80 italic">{data.switchChip}</p>
            )}
            {data.switchTitle && (
              <div className="richtext gap-[1.375rem] max-lg:[text-wrap:balance] max-lg:prose-p:w-[min(39.25rem,100%)] lg:richtext-start lg:gap-[2rem] lg:items-start lg:text-left prose-headings:my-0 prose-headings:font-geist prose-headings:font-semibold prose-headings:leading-[1.15] prose-headings:text-white max-lg:prose-headings:text-[4.5rem] max-lg:prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] prose-p:my-0 prose-p:font-geist prose-p:!text-[2.25rem] prose-p:leading-[1.5] prose-p:!text-white/70 [--hl-wash:4.5rem] lg:[--hl-wash:5.75rem] [--hl-tracking:-0.03em] [&_:is(h1,h2)_span]:leading-[0]">
                <PortableText value={data.switchTitle} components={competitorRichTextDark} />
              </div>
            )}
          </div>

          {(data.switchRating || data.switchSocialProof) && (
            <div className="flex w-full flex-col gap-[1rem] border-t border-white/10 pt-[3rem] text-left">
              <div className="flex items-center gap-[1.5rem]">
                <div className="flex items-center gap-[0.5rem]">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Star key={i} />
                  ))}
                </div>
                {data.switchRating && (
                  <p className="mb-0 font-geist text-[2.5rem] font-semibold leading-[1.2] text-white">{data.switchRating}</p>
                )}
              </div>
              {data.switchSocialProof && (
                <p className="mb-0 font-geist text-[2rem] leading-[1.5] whitespace-pre-line text-white/60">{data.switchSocialProof}</p>
              )}
            </div>
          )}

          {data.switchButton && (
            <CTAButton data={data.switchButton} overrideVariant="white" className="w-full lg:w-[min(23.125rem,100%)]" />
          )}
        </motion.div>

        <div className="flex w-full flex-col gap-[1.5rem] lg:w-[min(75rem,100%)] lg:shrink-0">
          {steps.map((step, index) => (
            <motion.div
              key={step._key ?? index}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease, delay: index * 0.08 }}>
              <StepCard step={step} />
            </motion.div>
          ))}
          {alternative?.title && (
            <>
              {data.switchAlternativeLabel && (
                <div className="flex items-center gap-[2rem] py-[1.5rem]">
                  <div className="h-px flex-1 bg-white/[0.12]" />
                  <p className="mb-0 font-geist text-[1.75rem] font-medium leading-none text-white/60 uppercase">
                    {data.switchAlternativeLabel}
                  </p>
                  <div className="h-px flex-1 bg-white/[0.12]" />
                </div>
              )}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, ease }}>
                <StepCard step={alternative} gold />
              </motion.div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
